<template>
	<div v-observe-visibility="moduleObserveVisibility">
		<Top10TitlesHero
			v-if="selectedTitle"
			:title="selectedTitle"
			:additionalContexts="heroAdditionalContexts"
			@titleClicked="onHeroTitleClicked"
		/>
		<div class="hero-titles container-fluid container-max-width">
			<HiddenHorizontalScrollbar ref="titlesContainer" fadedEdge :additionalContexts="[moduleContext]">
				<div
					@click="selectTitle(index)"
					v-for="(title, index) in topTitles"
					:key="title.id"
					class="top-10-jwtv-module__content_poster"
					:class="{ active: selectedIndex === index, last: index === topTitles.length - 1 }"
					:id="`title-poster-${index}`"
					v-observe-visibility="onVisibilityChanged({ title, index })"
				>
					<div class="title-poster__item-position">
						<span>
							{{ index + 1 }}
						</span>
					</div>
					<div class="title-poster__image-container" :class="{ active: selectedIndex === index }">
						<PictureComp
							v-if="title"
							class="title-poster__image"
							:alt="title.content.title"
							imageProfile="poster"
							:imageUrl="getImageUrl(title.content.posterUrl)"
							slug="Poster"
							:lazy="false"
						/>
						<div class="title-poster__bg">
							<div
								v-if="index === selectedIndex"
								class="title-poster__line"
								:key="`${selectedIndex}-${animationKey}`"
								@animationend="onProgressComplete"
							></div>
						</div>
					</div>
				</div>
			</HiddenHorizontalScrollbar>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { computed, ref, onDeactivated, onActivated, watch } from 'vue';
import { thumborFilter, type ThumborOptions } from '@/filters/thumbor';
import { useWindowFocus } from '@vueuse/core';

import PictureComp from '@/components/picture/PictureComp.vue';
import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';

import { SnowplowTitleContextGraphql } from '@/helpers/tracking/providers';
import { useHomeModuleTracking } from '@/features/onboarded-home/tracking';
import type { Top10ModuleInYourCountry } from '@/features/onboarded-home/types';
import { usePosterImpressionTracking } from '@/helpers/composables/usePosterImpressionTracking';
import Top10TitlesHero from '@/features/jwtv-titles-rental/components/modules/TitlesHero.vue';

const props = defineProps<{
	moduleData: Top10ModuleInYourCountry;
}>();

const SCROLL_OFFSET_PX = 20;

const selectedIndex = ref(0);
const titlesContainer = ref<{ scroller: HTMLElement } | null>(null);
const isPaused = ref(false);
const animationKey = ref(0);
const selectedTitleIndex = ref(0);

const windowFocused = useWindowFocus();

const { moduleContext, moduleObserveVisibility, trackHomeModule } = useHomeModuleTracking({
	moduleData: () => props.moduleData,
	globalPosition: 0,
	localPosition: 0,
	titleSize: () => props.moduleData.titles.length,
});

const topTitles = computed(() => {
	return props.moduleData.titles ?? [];
});

function getImageUrl(posterUrl: string | undefined | null) {
	return thumborFilter()({
		url: posterUrl,
		imageType: 'poster',
	} as ThumborOptions);
}

function onProgressComplete() {
	if (isPaused.value) return;
	nextPoster();
}

function nextPoster() {
	selectedIndex.value = (selectedIndex.value + 1) % topTitles.value.length;
	scrollToSelectedPoster(selectedIndex.value);
	onSelectedIndexUpdate(selectedIndex.value);
}

function selectTitle(index: number) {
	// If clicking on the same poster, reset the animation
	if (selectedIndex.value === index) {
		animationKey.value++;
	} else {
		selectedIndex.value = index;
		onSelectedIndexUpdate(index);
	}

	trackHomeModule('titleSelected', {
		spec: { label: moduleContext.value.type },
		contexts: [SnowplowTitleContextGraphql.fromTitle(topTitles.value[index], { index })],
	});
}

function scrollToSelectedPoster(index: number) {
	if (!titlesContainer.value?.scroller) return;

	const element = document.getElementById(`title-poster-${index}`);
	if (!element) return;

	const container = titlesContainer.value.scroller;
	const offsetLeft = element.offsetLeft - container.offsetLeft - SCROLL_OFFSET_PX;
	const isElementHidden =
		element.offsetLeft < container.scrollLeft ||
		element.offsetLeft + element.offsetWidth > container.scrollLeft + container.offsetWidth;

	if (isElementHidden) {
		container.scrollTo({
			left: offsetLeft,
			behavior: 'smooth',
		});
	}
}

const { onVisibilityChanged } = usePosterImpressionTracking({
	groupId: 'top_10_jwtv_module',
	preventDuplicateAdditionalContext: true,
	additionalContexts: () => [moduleContext.value],
});

const selectedTitle = computed(() => {
	if (!props.moduleData || !props.moduleData.titles) return null;
	return props.moduleData.titles[selectedTitleIndex.value];
});

const selectedTitleContextModule = computed(() => {
	if (!selectedTitle.value) return null;
	return SnowplowTitleContextGraphql.fromTitle(selectedTitle.value, {
		index: selectedTitleIndex.value,
	});
});

const heroAdditionalContexts = computed(() => {
	const contexts = [moduleContext.value, selectedTitleContextModule.value];
	return contexts.filter((ctx): ctx is NonNullable<typeof ctx> => ctx != null);
});

function onSelectedIndexUpdate(index: number) {
	selectedTitleIndex.value = index;
}

function onHeroTitleClicked() {
	if (!selectedTitleContextModule.value) return;

	trackHomeModule('titleClicked', {
		spec: { label: 'more_button_clicked' },
		contexts: [selectedTitleContextModule.value],
	});
}

watch(windowFocused, focused => {
	if (!focused) {
		isPaused.value = true;
	} else {
		isPaused.value = false;
		// Reset animation when returning to the tab
		animationKey.value++;
	}
});

onDeactivated(() => {
	isPaused.value = true;
});

onActivated(() => {
	isPaused.value = false;
});
</script>

<style lang="scss" scoped>
@keyframes progress-bar-animation {
	from {
		width: 0%;
	}
	to {
		width: 100%;
	}
}

.hero-titles {
	width: 100%;
	:deep(.hidden-horizontal-scrollbar__items) {
		display: flex;
		flex-direction: row;
		align-items: center;
		gap: 24px;
		height: 100%;
		min-height: 220px;
		overflow-y: hidden;
		@media screen and (max-width: 1050px) {
			gap: 16px;
		}
	}
}

.top-10-jwtv-module {
	&__content_poster {
		display: flex;
		position: relative;
		align-self: flex-end;
		border-radius: 8px;
		cursor: pointer;
		&.last {
			margin-inline-end: 50px;
		}

		.title-poster__image-container {
			position: relative;
			width: 128px;
			height: 192px;
			transition: width 0.25s ease-in-out, height 0.25s ease-in-out;

			&.active {
				width: 144px;
				height: 216px;
				.title-poster__bg {
					background: transparent;
				}
			}
		}
		.title-poster__image {
			position: relative;
			:deep(img) {
				border-radius: 8px;
			}
		}
		.title-poster__item-position {
			color: #222c38;
			right: 100%;
			line-height: 100%;
			font-size: 120px;
			letter-spacing: -20px;
			margin-inline-end: -2px;
			font-weight: 900;
			margin-inline-start: -4px;
			display: flex;
			align-items: flex-end;
			justify-content: flex-end;
		}
		.title-poster__bg {
			position: absolute;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
			background: linear-gradient(0deg, rgba(6, 13, 23, 0.64) 0%, rgba(6, 13, 23, 0.64) 100%);
			border-radius: 8px;
			z-index: 1;
			overflow: hidden;
		}
		.title-poster__line {
			height: 3px;
			background-color: #fbc500;
			animation: progress-bar-animation 8s linear;
			z-index: 3;
		}
	}
}
</style>
